import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})
const BACKEND_API_URL = import.meta.env?.VITE_API_URL || 'http://localhost:4000';
const USE_BACKEND_AUTH = import.meta.env?.VITE_AUTH_PROVIDER === 'backend';

// Pro user emails with permanent admin access
const PRO_USER_EMAILS = ['travelbagbiggz2025@gmail.com', 'lewda3rd@gmail.com'];

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  const normalizeBackendUser = (backendUser) => {
    if (!backendUser) return null;
    return {
      ...backendUser,
      user_metadata: {
        full_name: backendUser?.full_name,
        role: backendUser?.role,
      },
    };
  };

  // Check if user is a pro user (admin with unlimited access)
  const isProUser = (email) => {
    return PRO_USER_EMAILS?.includes(email?.toLowerCase());
  };

  // Check if user has pro access (either pro user or valid subscription)
  const hasProAccess = () => {
    if (!user || !userProfile) return false;
    
    // Pro users always have access
    if (isProUser(user?.email)) return true;
    
    // Check subscription status
    if (userProfile?.role === 'admin' || userProfile?.role === 'premium') {
      if (!userProfile?.subscription_expires_at) return true;
      return new Date(userProfile?.subscription_expires_at) > new Date();
    }
    
    return false;
  };

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        if (!error) setUserProfile(data)
      } catch (error) {
        console.error('Profile load error:', error)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  useEffect(() => {
    const backendToken = localStorage.getItem('auth_token');
    if (USE_BACKEND_AUTH && backendToken) {
      fetch(`${BACKEND_API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${backendToken}` },
      })
        .then(async (response) => {
          if (!response.ok) throw new Error('Backend session expired');
          const data = await response.json();
          const backendUser = normalizeBackendUser(data?.user);
          setUser(backendUser);
          setUserProfile(data?.user || null);
          setLoading(false);
        })
        .catch(() => {
          localStorage.removeItem('auth_token');
          supabase?.auth?.getSession()?.then(({ data: { session } }) => {
            authStateHandlers?.onChange(null, session);
          });
        });
      return;
    }

    // Initial session check
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session)
    })

    // CRITICAL: This must remain synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      if (USE_BACKEND_AUTH) {
        const backendResponse = await fetch(`${BACKEND_API_URL}/auth/signin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
        const backendData = await backendResponse.json().catch(() => ({}));

        if (!backendResponse.ok) {
          return { data: null, error: { message: backendData?.error || 'Invalid email or password' } };
        }

        localStorage.setItem('auth_token', backendData.token);
        const backendUser = normalizeBackendUser(backendData.user);
        setUser(backendUser);
        setUserProfile(backendData.user || null);
        return { data: { user: backendUser, session: { user: backendUser } }, error: null };
      }

      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signUp = async ({ email, password, fullName }) => {
    try {
      if (USE_BACKEND_AUTH) {
        const backendResponse = await fetch(`${BACKEND_API_URL}/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password, full_name: fullName }),
        });
        const backendData = await backendResponse.json().catch(() => ({}));

        if (!backendResponse.ok) {
          return { data: null, error: { message: backendData?.error || 'Account creation failed' } };
        }

        localStorage.setItem('auth_token', backendData.token);
        const backendUser = normalizeBackendUser(backendData.user);
        setUser(backendUser);
        setUserProfile(backendData.user || null);
        return { data: { user: backendUser, session: { user: backendUser } }, error: null };
      }

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'free',
          },
        },
      });
      return { data, error };
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } };
    }
  }

  const signOut = async () => {
    try {
      const backendToken = localStorage.getItem('auth_token');
      if (backendToken) {
        await fetch(`${BACKEND_API_URL}/auth/signout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${backendToken}` },
        }).catch(() => null);
        localStorage.removeItem('auth_token');
      }

      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    updateProfile,
    isAuthenticated: !!user,
    isProUser: isProUser(user?.email),
    hasProAccess: hasProAccess(),
    authProvider: USE_BACKEND_AUTH ? 'backend' : 'supabase',
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
